import { useMutation } from '@tanstack/react-query';
import { useApiClientContext } from '../context';

export interface IAnonymousCheckInRequest {
	eventId: string;
	confirmationNumber: string;
	attendeeIndex?: number;
}

export interface IRegistrationData {
	registrationId: string;
	confirmationNumber: string;
	isAttending: boolean;
	attendees: {
		index: number;
		name: string;
		checkInStatus: 'Unregistered' | 'Registered' | 'CheckedIn' | number;
		isPrimary: boolean;
	}[];
}

export interface IRegistrationLookupResponse {
	success: boolean;
	systemCode?: number;
	requestId?: string;
	value?: IRegistrationData;
	systemMessage?: string;
}

export interface IAnonymousCheckInResponse {
	success: boolean;
	alreadyCheckedIn?: boolean;
	attendeeName?: string;
	error?: string;
	registration?: IRegistrationData;
}

// Global cache to store results and prevent duplicate calls
const processedRequests = new Map<string, Promise<IAnonymousCheckInResponse>>();

export const useAnonymousCheckInMutation = () => {
	const { apiClient } = useApiClientContext();

	return useMutation<IAnonymousCheckInResponse, Error, IAnonymousCheckInRequest>({
		mutationFn: async ({ eventId, confirmationNumber, attendeeIndex = 0 }) => {
			// Create a unique key for this request
			const requestKey = `${eventId}-${confirmationNumber}-${attendeeIndex}`;

			// Check if we've already processed this exact request
			if (processedRequests.has(requestKey)) {
				return processedRequests.get(requestKey)!;
			}

			// Create the promise for this request
			const requestPromise = (async (): Promise<IAnonymousCheckInResponse> => {
				try {
					// First, get the registration details to check current status
					const registrationResponse = await fetch(
						`${apiClient.baseUrl}/public/eventRegistration/${eventId}/checkin/${confirmationNumber}`
					);

					if (!registrationResponse.ok) {
						throw new Error('Registration not found');
					}

					const response: IRegistrationLookupResponse = await registrationResponse.json();

					// Check if the API call was successful
					if (!response.success) {
						throw new Error(response.systemMessage || 'Registration lookup failed');
					}

					// Ensure we have the registration data
					if (!response.value) {
						throw new Error('No registration data returned');
					}

					const registration = response.value;

					// Ensure attendees array exists
					if (!registration.attendees || !Array.isArray(registration.attendees)) {
						throw new Error('Invalid registration data: attendees array missing');
					}

					// Find the attendee by index
					const targetAttendee = registration.attendees.find(attendee => attendee.index === attendeeIndex);

					if (!targetAttendee) {
						throw new Error('Invalid attendee index');
					}

					// Check if already checked in (status can be string or number)
					const isCheckedIn = targetAttendee.checkInStatus === 'CheckedIn' || targetAttendee.checkInStatus === 2;

					if (isCheckedIn) {
						return {
							success: false,
							alreadyCheckedIn: true,
							attendeeName: targetAttendee.name,
							registration,
						};
					}

					// Perform the check-in
					const checkInResponse = await fetch(
						`${apiClient.baseUrl}/public/eventRegistration/${eventId}/checkin/${confirmationNumber}/attendee/${attendeeIndex}`,
						{
							method: 'PUT',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify('CheckedIn'),
						}
					);

					if (!checkInResponse.ok) {
						const errorData = await checkInResponse.json();
						throw new Error(errorData.systemMessage || 'Check-in failed');
					}

					return {
						success: true,
						attendeeName: targetAttendee.name,
						registration,
					};
				} catch (error: any) {
					// Handle specific API error cases
					if (error.message.includes('Registration not found')) {
						throw new Error('Invalid event ID or confirmation number');
					}
					throw new Error(error.message || 'Check-in failed');
				}
			})();

			// Store the promise in cache
			processedRequests.set(requestKey, requestPromise);

			// Return the result
			try {
				const result = await requestPromise;
				// Keep successful results in cache briefly, then clean up
				setTimeout(() => processedRequests.delete(requestKey), 5000);
				return result;
			} catch (error) {
				// Remove failed requests immediately
				processedRequests.delete(requestKey);
				throw error;
			}
		},
	});
};
